import { useMutation, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import type { ParentData } from "features/cases/blocks/CommonCard/CommonCard.types";
import {
  applySavedItemToCache,
  postCardContent,
} from "features/cases/blocks/CommonCard/CommonCard.utils";
import { EDITORIAL_UNREVIEWED_QUERY_KEY } from "features/editorial/types";
import { useUser } from "hooks/useUser";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { validateCardContent } from "utils/card-content-validation";

const collectivePersonId = 2;

// Строит тело запроса для создания/редактирования — используется и явным
// сохранением, и автосохранением (useAutosave), чтобы оба пути были идентичны.
export const buildCardSavePayload = ({
  editedContent,
  selectedSphere,
  isDraftOverride,
  parentData,
  selectedVariant,
  titleInput,
  isUsingCollectivePerson,
  personId,
}: {
  editedContent: string;
  selectedSphere: number | null;
  isDraftOverride?: boolean;
  parentData: ParentData;
  selectedVariant: number | null;
  titleInput: string;
  isUsingCollectivePerson: boolean;
  personId: number | null | undefined;
}): Record<string, unknown> => ({
  description: editedContent,
  sphere: selectedSphere,
  ...(isDraftOverride !== undefined ? { isDraft: isDraftOverride } : {}),
  attributeType: parentData.attributeType,
  attributeOf: parentData.attributeOf,
  problemId: Number(parentData.problemId),
  solutionId: Number(parentData.solutionId),
  variantId: selectedVariant,
  title: titleInput,
  author: isUsingCollectivePerson ? collectivePersonId : personId,
});

type UseCardSaveParams = {
  editContentUrl: string;
  parentData: ParentData;
  selectedSphere: number | null;
  selectedVariant: number | null;
  isUsingCollectivePerson: boolean;
  editedContent: string;
  titleInput: string;
  isCard: boolean;
  isDraft: boolean;
  isMyStuff: boolean | null | undefined;
  isReviewingMode: boolean;
  isCreating: boolean | string | null | undefined;
  queryId: string | undefined;
  closeModal: () => void;
  onSaved?: () => void;
  refetchGeneral: () => void;
};

export const useCardSave = ({
  editContentUrl,
  parentData,
  selectedSphere,
  selectedVariant,
  isUsingCollectivePerson,
  editedContent,
  titleInput,
  isCard,
  isDraft,
  isMyStuff,
  isReviewingMode,
  isCreating,
  queryId,
  closeModal,
  onSaved,
  refetchGeneral,
}: UseCardSaveParams) => {
  const { personId, isBannedFromPublishing } = useUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const mutation = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      postCardContent(editContentUrl, payload),
    onError: (error: Error) => {
      message.error(error.message);
    },
    onSuccess: (updatedItem) => {
      const isNewSolutionCreated =
        parentData.isSolution && !parentData.solutionId;
      const isNewProblemCreated = parentData.isProblem && !parentData.problemId;

      // closeModal() тут вызывает асинхронный navigate(-1), который срабатывает
      // ПОСЛЕ синхронного navigate(путь) ниже и уводит обратно со страницы.
      if (!isNewSolutionCreated && !isNewProblemCreated) {
        closeModal();
      }
      onSaved?.();

      if (parentData.fromEditorialFeed) {
        queryClient.invalidateQueries({
          queryKey: [EDITORIAL_UNREVIEWED_QUERY_KEY],
        });
      }

      if (parentData.isSolution || parentData.isProblem) refetchGeneral();

      if (isNewSolutionCreated) {
        navigate(
          `/problems/${updatedItem?.problemId}/solutions/${updatedItem?.id}/`,
        );
      }

      if (parentData.isProblem) {
        if (isNewProblemCreated) {
          navigate(`/problems/${updatedItem?.id}`);
        }
        queryClient.refetchQueries({
          queryKey: [`problems/${updatedItem?.id}`],
        });
      }

      applySavedItemToCache(queryClient, parentData, queryId, updatedItem);
    },
  });

  const contentValidation = useMemo(
    () =>
      validateCardContent({
        title: titleInput,
        description: editedContent,
        isCard,
      }),
    [titleInput, editedContent, isCard],
  );

  const submit = useCallback(
    (isDraftOverride?: boolean) => {
      if (!contentValidation.canSubmit) return;
      mutation.mutate(
        buildCardSavePayload({
          editedContent,
          selectedSphere,
          isDraftOverride,
          parentData,
          selectedVariant,
          titleInput,
          isUsingCollectivePerson,
          personId,
        }),
      );
    },
    [
      editedContent,
      titleInput,
      selectedSphere,
      selectedVariant,
      contentValidation.canSubmit,
      mutation,
      parentData,
      isUsingCollectivePerson,
      personId,
    ],
  );

  const onSaveAndPublishClick = useCallback(() => submit(false), [submit]);
  const onSaveToDraftClick = useCallback(() => submit(true), [submit]);
  const onSaveEditedClick = useCallback(() => submit(), [submit]);

  const canShowPublishButton =
    isCard &&
    !isBannedFromPublishing &&
    ((isDraft && !!isMyStuff) || !!isCreating);
  const canShowSaveToDraftButton =
    isCard && ((isDraft && !!isMyStuff) || !!isCreating);
  const canShowSaveEditedButton =
    !canShowSaveToDraftButton &&
    (!isDraft || !isCard) &&
    (!!isMyStuff || isReviewingMode || !!isCreating);

  return {
    onSaveAndPublishClick,
    onSaveToDraftClick,
    onSaveEditedClick,
    canShowPublishButton,
    canShowSaveToDraftButton,
    canShowSaveEditedButton,
    contentIsSubmittable: contentValidation.canSubmit,
    submitTooltipText: contentValidation.submitTooltipText,
    contentValidation,
  };
};
